import { notifications } from "@mantine/notifications"
import { CheckCircle } from "lucide-react"
import { createElement } from "react"
import { create } from "zustand"

// Interfaces
export interface WeeklyVisitor {
  day: string
  visitors: number
  cost: number
}

export interface TopService {
  name: string
  cost: number
  percentage: number
}

export type RecommendationPriority = "high" | "medium" | "low"
export type RecommendationStatus = "pending" | "applied"

interface BaseRecommendation {
  id: string
  department: string
  service: string
  /** Estimated monthly savings in USD if this recommendation is applied */
  monthlySavings: number
  priority: RecommendationPriority
  status: RecommendationStatus
  /** True when this recommendation was produced by the AI cost-analysis agent rather than a static rule */
  aiGenerated: boolean
}

/** A concrete "switch from X to Y" instance right-sizing recommendation */
export interface InstanceChangeRecommendation extends BaseRecommendation {
  type: "instance_change"
  instanceName: string
  currentInstanceType: string
  recommendedInstanceType: string
  reason: string
}

/** A non instance-specific recommendation (lifecycle policies, unused resources, etc.) */
export interface GeneralRecommendation extends BaseRecommendation {
  type: "general"
  title: string
  description: string
}

export type CostRecommendation = InstanceChangeRecommendation | GeneralRecommendation

export interface DepartmentSpend {
  department: string
  label: string
  monthlySpend: number
}

export interface DepartmentCostBreakdown extends DepartmentSpend {
  percentageOfTotalSpend: number
  potentialMonthlySavings: number
  potentialAnnualSavings: number
  savingsPercentageOfDeptSpend: number
  recommendationCount: number
}

export interface CostAnalysis {
  currentMonth: {
    total: number
    change: number
    trend: "up" | "down"
  }
  previousMonth: {
    total: number
  }
  topServices: TopService[]
  recommendations: CostRecommendation[]
  departmentSpend: DepartmentSpend[]
}

// Store Interface
interface CostAdvisorStore {
  // State
  weeklyVisitors: WeeklyVisitor[]
  costAnalysis: CostAnalysis
  isLoading: boolean
  error: string | null

  // Actions
  fetchWeeklyVisitors: () => Promise<void>
  fetchCostAnalysis: () => Promise<void>
  applyRecommendation: (id: string) => Promise<void>
  reset: () => void
}

// Mock Data
const mockWeeklyVisitors: WeeklyVisitor[] = [
  { day: "Mon", visitors: 450, cost: 125 },
  { day: "Tue", visitors: 380, cost: 105 },
  { day: "Wed", visitors: 520, cost: 145 },
  { day: "Thu", visitors: 490, cost: 135 },
  { day: "Fri", visitors: 610, cost: 170 },
  { day: "Sat", visitors: 320, cost: 90 },
  { day: "Sun", visitors: 280, cost: 78 },
]

const mockCostAnalysis: CostAnalysis = {
  currentMonth: {
    total: 4250.75,
    change: 12.5,
    trend: "up",
  },
  previousMonth: {
    total: 3780.25,
  },
  topServices: [
    { name: "EC2 Instances", cost: 1850.0, percentage: 43.5 },
    { name: "S3 Storage", cost: 980.5, percentage: 23.1 },
    { name: "RDS Database", cost: 720.25, percentage: 16.9 },
    { name: "Lambda Functions", cost: 450.0, percentage: 10.6 },
    { name: "CloudFront", cost: 250.0, percentage: 5.9 },
  ],
  // Full-spend breakdown by department, used to power the department savings view
  departmentSpend: [
    { department: "engineering", label: "Engineering", monthlySpend: 1912.84 },
    { department: "operations", label: "Operations", monthlySpend: 850.15 },
    { department: "sales", label: "Sales", monthlySpend: 637.61 },
    { department: "marketing", label: "Marketing", monthlySpend: 510.09 },
    { department: "finance", label: "Finance", monthlySpend: 340.06 },
  ],
  recommendations: [
    {
      id: "1",
      type: "instance_change",
      department: "engineering",
      service: "ec2",
      instanceName: "web-server-01",
      currentInstanceType: "m5.xlarge",
      recommendedInstanceType: "m5.large",
      reason: "Average CPU utilization was 12% over the last 14 days",
      monthlySavings: 180,
      priority: "high",
      status: "pending",
      aiGenerated: true,
    },
    {
      id: "2",
      type: "instance_change",
      department: "engineering",
      service: "ec2",
      instanceName: "api-worker-03",
      currentInstanceType: "t3.medium",
      recommendedInstanceType: "t3.small",
      reason: "Memory and CPU consistently under 25% of provisioned capacity",
      monthlySavings: 42,
      priority: "medium",
      status: "pending",
      aiGenerated: true,
    },
    {
      id: "3",
      type: "instance_change",
      department: "operations",
      service: "ec2",
      instanceName: "batch-processor-02",
      currentInstanceType: "c5.large",
      recommendedInstanceType: "t3.medium",
      reason: "Batch workload is I/O bound, not compute bound — compute-optimized family is unnecessary",
      monthlySavings: 95,
      priority: "high",
      status: "pending",
      aiGenerated: true,
    },
    {
      id: "4",
      type: "instance_change",
      department: "sales",
      service: "ec2",
      instanceName: "crm-db-replica",
      currentInstanceType: "r5.large",
      recommendedInstanceType: "m5.large",
      reason: "Memory-optimized family not justified by read-replica working set size",
      monthlySavings: 65,
      priority: "medium",
      status: "pending",
      aiGenerated: true,
    },
    {
      id: "5",
      type: "instance_change",
      department: "marketing",
      service: "ec2",
      instanceName: "cms-app-01",
      currentInstanceType: "t2.medium",
      recommendedInstanceType: "t3.small",
      reason: "Legacy t2 family — t3 offers the same burst credits at lower on-demand cost",
      monthlySavings: 33,
      priority: "low",
      status: "pending",
      aiGenerated: true,
    },
    {
      id: "6",
      type: "general",
      department: "operations",
      service: "s3",
      title: "Enable S3 Lifecycle Policies",
      description: "Archive infrequently accessed objects in the ops-logs bucket to Glacier",
      monthlySavings: 55,
      priority: "medium",
      status: "pending",
      aiGenerated: true,
    },
    {
      id: "7",
      type: "general",
      department: "finance",
      service: "ec2",
      title: "Remove Unattached EBS Volumes",
      description: "4 unattached volumes have been idle for over 30 days",
      monthlySavings: 28,
      priority: "low",
      status: "pending",
      aiGenerated: false,
    },
  ],
}

// Initial State
const initialState = {
  weeklyVisitors: [],
  costAnalysis: {
    currentMonth: {
      total: 0,
      change: 0,
      trend: "up" as const,
    },
    previousMonth: {
      total: 0,
    },
    topServices: [],
    recommendations: [],
    departmentSpend: [],
  },
  isLoading: false,
  error: null,
}

// Store
export const useCostAdvisorStore = create<CostAdvisorStore>((set) => ({
  ...initialState,

  // Actions
  fetchWeeklyVisitors: async () => {
    set({ isLoading: true, error: null })
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 300))
      set({ weeklyVisitors: mockWeeklyVisitors, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Failed to fetch weekly visitors",
        isLoading: false,
      })
      throw error
    }
  },

  fetchCostAnalysis: async () => {
    set({ isLoading: true, error: null })
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 400))
      set({ costAnalysis: mockCostAnalysis, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Failed to fetch cost analysis",
        isLoading: false,
      })
      throw error
    }
  },

  applyRecommendation: async (id) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 300))
    set((state) => ({
      costAnalysis: {
        ...state.costAnalysis,
        recommendations: state.costAnalysis.recommendations.map((rec) =>
          rec.id === id ? { ...rec, status: "applied" as const } : rec
        ),
      },
    }))
    notifications.show({
      title: "Recommendation Applied",
      message: "This optimization will be reflected in next month's spend.",
      color: "teal",
      icon: createElement(CheckCircle, { size: 18 }),
      autoClose: 4000,
      withCloseButton: true,
    })
  },

  reset: () => set(initialState),
}))

// Selectors
export const selectWeeklyVisitors = (state: CostAdvisorStore) => state.weeklyVisitors
export const selectCostAnalysis = (state: CostAdvisorStore) => state.costAnalysis

export const selectTotalPotentialMonthlySavings = (state: CostAdvisorStore) =>
  state.costAnalysis.recommendations
    .filter((rec) => rec.status === "pending")
    .reduce((sum, rec) => sum + rec.monthlySavings, 0)

export const selectProjectedAnnualSavings = (state: CostAdvisorStore) =>
  selectTotalPotentialMonthlySavings(state) * 12

export const selectSavingsPercentageOfSpend = (state: CostAdvisorStore) => {
  const total = state.costAnalysis.currentMonth.total
  return total > 0 ? (selectTotalPotentialMonthlySavings(state) / total) * 100 : 0
}

export const selectHighPriorityRecommendations = (state: CostAdvisorStore) =>
  state.costAnalysis.recommendations.filter((rec) => rec.priority === "high")

export const selectCostTrend = (state: CostAdvisorStore) => state.costAnalysis.currentMonth.trend

/**
 * Pure derivation, safe to call from `useMemo` keyed on `costAnalysis`. Not for direct use as a
 * store selector hook (`useCostAdvisorStore(fn)`) — it allocates a new array every call, which
 * breaks Zustand/useSyncExternalStore's reference-stability contract and causes a render loop.
 */
export const buildDepartmentBreakdown = (costAnalysis: CostAnalysis): DepartmentCostBreakdown[] => {
  const totalSpend = costAnalysis.currentMonth.total
  return costAnalysis.departmentSpend.map((dept) => {
    const deptRecommendations = costAnalysis.recommendations.filter(
      (rec) => rec.department === dept.department && rec.status === "pending"
    )
    const potentialMonthlySavings = deptRecommendations.reduce((sum, rec) => sum + rec.monthlySavings, 0)
    return {
      ...dept,
      percentageOfTotalSpend: totalSpend > 0 ? (dept.monthlySpend / totalSpend) * 100 : 0,
      potentialMonthlySavings,
      potentialAnnualSavings: potentialMonthlySavings * 12,
      savingsPercentageOfDeptSpend: dept.monthlySpend > 0 ? (potentialMonthlySavings / dept.monthlySpend) * 100 : 0,
      recommendationCount: deptRecommendations.length,
    }
  })
}

/** Convenience wrapper for one-off reads (e.g. `selectDepartmentBreakdown(useCostAdvisorStore.getState())`) — do not pass directly to the `useCostAdvisorStore` hook, see `buildDepartmentBreakdown`. */
export const selectDepartmentBreakdown = (state: CostAdvisorStore): DepartmentCostBreakdown[] =>
  buildDepartmentBreakdown(state.costAnalysis)
